import { useRef } from "react"
import { useIntersectionObserver } from "../../../lib/use-intersection-observer"
import clsx from "clsx"
import styles from "./hero-small.module.css"
import { PageBreadcrumb } from "../pagebreadcrumb"
import PatternProps from "../../../assets/patterns/PatternProps"
import EnumPatternType from "../../../assets/patterns/EnumPatternType"
import useGetPattern from "../../../lib/use-get-pattern"

type HeroIconTypes = "email" | "chat" | "call"

export type HeroSmallData = {
  id?: string
  breadcrumb?: {
    url: string
    label: string
  }
  breadcrumbs: {
    items: {
      link: {
        active: boolean;
        url: string,
      },
      title: string
    }[]
  },
  displayPattern?: boolean
  patternId: string
  eyebrow?: string
  title: string
  hideBreadcrumb?: boolean;
  descriptionHTML: { __html: string | TrustedHTML }
  ctas?: {
    url: string
    fn?: () => void
    label: string
    openNewTab?: boolean
    iconType: HeroIconTypes
  }[]
}

type BreadcrumbProps = {
  breadcrumbs?: HeroSmallData["breadcrumbs"]
  theme: "light" | "dark"
}

type IconSvgProps = {
  className?: string
}

type CtaIconProps = {
  iconType: HeroIconTypes
  className: string
}

type CtaButtonsProps = {
  ctas?: HeroSmallData["ctas"]
}

type HeroSmallProps = {
  data: HeroSmallData
  animateIntoView?: boolean
}

const HeroPattern = (props: PatternProps) => {
  const usePatternProps = {
    ...props,
    classes: clsx(styles.patternSvg, { [styles.withAnimation]: props.isVisible }),
    patternType: EnumPatternType.heroSmall
  }

  return useGetPattern(usePatternProps)
}

const EnvelopeIconSvg = ({ className }: IconSvgProps) => (
  <svg
    className={className}
    width="53"
    height="54"
    viewBox="0 0 53 54"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect className={styles.svgRect} y="0.333984" width="53" height="53" rx="26.5" />
    <path
      className={styles.svgStroke}
      d="M35.7967 19.334C36.6139 19.334 37.2822 20.0023 37.2822 20.8194V32.7033C37.2822 33.5215 36.6139 34.1888 35.7967 34.1888H16.4855C15.6683 34.1888 15 33.5215 15 32.7033V20.8194C15 20.0023 15.6683 19.334 16.4855 19.334H35.7967Z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      className={styles.svgStroke}
      d="M36.5399 20.0762L26.1415 28.989L15.7432 20.0762"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const ChatIconSvg = ({ className }: IconSvgProps) => (
  <svg
    className={className}
    width="53"
    height="53"
    viewBox="0 0 53 53"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect className={styles.svgRect} width="53" height="53" rx="26.5" />
    <path
      className={styles.svgStroke}
      d="M23.9444 33.7788H26.5021H26.5C32.8513 33.7788 38 29.7743 38 24.8328C38 19.8913 32.8513 15.8848 26.5 15.8848C20.1487 15.8848 15 19.8924 15 24.8328C15 27.8017 16.6399 30.2968 19.5007 31.9246C19.2987 35.0565 17.5555 36.8848 17.5555 36.8848C17.5555 36.8848 21.4577 36.8401 23.9444 33.7778V33.7788Z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const PhoneIconSvg = ({ className }: IconSvgProps) => (
  <svg
    className={className}
    width="53"
    height="54"
    viewBox="0 0 53 54"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect className={styles.svgRect} y="0.667969" width="53" height="53" rx="26.5" />
    <path
      className={styles.svgStroke}
      d="M18.1629 14.8878L14.4377 18.1836L14.4168 18.2064C12.8063 19.8849 16.0004 25.944 21.6316 31.8104C27.2648 37.6788 33.2054 41.1415 34.8158 39.463L34.8377 39.4391L38 35.533L32.2604 30.6468L28.6077 33.3645L20.2599 24.6696L22.8691 20.865L18.162 14.8867L18.1629 14.8878Z"
      stroke="#4196B4"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CtaIcon = ({ iconType, className }: CtaIconProps) => {
  switch (iconType) {
    case "email":
      return <EnvelopeIconSvg className={className} />
    case "chat":
      return <ChatIconSvg className={className} />
    case "call":
      return <PhoneIconSvg className={className} />
    default:
      return null
  }
}

const CtaButtons = ({ ctas }: CtaButtonsProps) => (
  <div className={styles.ctaButtonsContainer}>
    {ctas?.map((cta, ctaIndex) => (
      <a key={ctaIndex} target={cta.openNewTab ? "_blank" : "_self"} rel="noreferrer" href={cta.url} className={styles.ctaButton} onClick={cta?.fn ?? undefined}>
        <CtaIcon iconType={cta.iconType} className={styles.ctaIcon} />
        {cta.label}
      </a>
    ))}
  </div>
)

const Breadcrumb = ({ breadcrumbs, theme }: BreadcrumbProps) => (

  <nav className={styles.breadcrumbContainer}>
    {breadcrumbs && (
      <PageBreadcrumb items={breadcrumbs.items} theme={theme} />
    )}

  </nav>
)

export const HeroSmall = ({ data, animateIntoView = true }: HeroSmallProps) => {
  const { id, breadcrumbs, displayPattern, patternId, eyebrow, title, descriptionHTML, ctas, hideBreadcrumb } = data
  const ref = useRef<HTMLDivElement | null>(null)
  const viewportEntry = useIntersectionObserver(ref, {
    freezeOnceVisible: true,
  })
  const isVisible = !!viewportEntry?.isIntersecting
  const patternProps: PatternProps = { patternId, isVisible }

  const hasCtas = !!ctas && ctas?.length > 0

  return (
    <section ref={ref} id={id ?? undefined} className={styles.container}>
      {!hideBreadcrumb && breadcrumbs && breadcrumbs.items.length > 0 && (
        <Breadcrumb breadcrumbs={breadcrumbs} theme="dark"></Breadcrumb>
      )}
      <div className={clsx("defaultLayoutOuter", styles.layoutOuter)}>
        {displayPattern && !hasCtas && (
          <div className={styles.pattern} data-testid="pattern">
            <HeroPattern {...patternProps} />
          </div>
        )}
        <div
          className={clsx("defaultLayoutInner defaultGrid", styles.layoutInner, {
            defaultAnimatableIntoView: animateIntoView,
            defaultPlayAnimationIntoView: animateIntoView && isVisible,
          })}
          data-testid="layoutInner"
        >

          <div
            className={clsx(styles.textContainer, {
              [styles.narrow]: hasCtas,
            })}
          >
            {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
            <h1 className={styles.title}>{title}</h1>
            <div className={styles.description} dangerouslySetInnerHTML={descriptionHTML} />
          </div>
          {hasCtas && <CtaButtons ctas={ctas} />}
        </div>
      </div>
    </section>
  )
}
